import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSegments,
  getSegment,
  createSegment,
  updateSegment,
  deleteSegment,
  getSegmentContacts,
  refreshSegmentCount,
} from "api/bMarketingApi";

export function useSegments(organisationId, options = {}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["segments", organisationId],
    queryFn: () => getSegments(organisationId).then((res) => res.data),
    enabled: !!organisationId && options.enabled !== false,
  });

  return {
    segments: data || [],
    isLoading,
    error,
    refetch,
  };
}

export function useSegmentDetail(segmentId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["segment", segmentId],
    queryFn: () => getSegment(segmentId).then((res) => res.data),
    enabled: !!segmentId,
  });

  return { segment: data, isLoading, error, refetch };
}

export function useSegmentContacts(segmentId, filters = {}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["segmentContacts", segmentId, filters],
    queryFn: () => getSegmentContacts(segmentId, filters).then((res) => res.data),
    enabled: !!segmentId,
  });

  return {
    contacts: data?.results || [],
    pagination: {
      count: data?.count || 0,
      numPages: data?.num_pages || 1,
      currentPage: data?.current_page || 1,
    },
    segmentName: data?.segment_name || "",
    isLoading,
    error,
    refetch,
  };
}

export function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createSegment(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
  });
}

export function useUpdateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, data }) => updateSegment(segmentId, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["segments"] });
      queryClient.invalidateQueries({ queryKey: ["segment", variables.segmentId] });
      queryClient.invalidateQueries({ queryKey: ["segmentContacts", variables.segmentId] });
    },
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (segmentId) => deleteSegment(segmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
  });
}

export function useRefreshSegmentCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (segmentId) => refreshSegmentCount(segmentId).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["segments"] });
    },
  });
}
